import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Tabs } from '@parspec/pixel';

import { useCheckAccessForProductFinderOrQuoting } from 'src/features/shared/hooks/useCheckAccessForProductFinderOrQuoting';
import { useFeatureFlag } from 'src/features/shared/UserActivityTracking/useFeatureFlag';
import { DisabledTab } from '../shared/components/DisabledTab';
import { useLocationDetails } from './context';
import { useParspecNavigation } from 'src/app/Context';
import { generateBaseEditLocUrl } from '../utils/utils';

const ADD_DUPLICATE_LOC_DEFAULT_TAB_OPTIONS = [
    { label: 'Branch Details', value: '/v2/settings/company-locations/add' },
    { label: <DisabledTab tabName={'Branch Branding'} />, value: '', },
    { label: <DisabledTab tabName={'BOM Settings'} />, value: '', },
    { label: <DisabledTab tabName={'Submittal Customization'} />, value: '' },
    { label: <DisabledTab tabName={'O&M Customization'} />, value: '' }
];

export const NavTabs = () => {
    const { enable_quoting } = useFeatureFlag();
    const { quotingAccess } = useCheckAccessForProductFinderOrQuoting();

    const { pNavigate } = useParspecNavigation();

    const { currentLocationId, isDuplicate } = useLocationDetails();


    const [tabOptionsState, setTabOptionsState] = useState<{ label: string | JSX.Element; value: string; }[]>(ADD_DUPLICATE_LOC_DEFAULT_TAB_OPTIONS)

    const { pathname } = useLocation();

    const handleTabChange = (tabValue: string) => {
        if (currentLocationId === 0 || isDuplicate) {
            return
        }
        pNavigate(tabValue);
    };

    const TAB_VALUES = useMemo(() => {
        const values = {
            BRANCH_DETAILS_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/branchDetails`,
            BRANCH_BRANDING_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/branchBranding`,
            BOM_SETTINGS_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/bomSettings`,
            QUOTE_CUSTOMISATION_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/quoteCustomisation`,
            SUBMITTAL_CUSTOMISATION_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/submittalCustomisation`,
            OM_CUSTOMISATION_TAB_VALUE: `${generateBaseEditLocUrl(currentLocationId)}/omCustomisation`
        };

        return values
    }, [currentLocationId])


    useEffect(() => {
        const EDIT_LOC_DEFAULT_TAB_OPTIONS = [
            { label: 'Branch Details', value: TAB_VALUES.BRANCH_DETAILS_TAB_VALUE },
            { label: 'Branch Branding', value: TAB_VALUES.BRANCH_BRANDING_TAB_VALUE },
            { label: 'BOM Settings', value: TAB_VALUES.BOM_SETTINGS_TAB_VALUE },
            { label: 'Submittal Customization', value: TAB_VALUES.SUBMITTAL_CUSTOMISATION_TAB_VALUE },
            { label: 'O&M Customization', value: TAB_VALUES.OM_CUSTOMISATION_TAB_VALUE }
        ];

        // if add-location or duplicate-location
        if (currentLocationId === 0 || isDuplicate) {
            if (enable_quoting && quotingAccess) {
                const quoteCustomisationTabValue = { label: <DisabledTab tabName={'Quote Customization'} />, value: '', }
                const addDuplicateOptions = [...ADD_DUPLICATE_LOC_DEFAULT_TAB_OPTIONS]
                addDuplicateOptions.splice(3, 0, quoteCustomisationTabValue)
                setTabOptionsState(() => addDuplicateOptions)
                return
            } else {
                setTabOptionsState(() => ADD_DUPLICATE_LOC_DEFAULT_TAB_OPTIONS)
            }
            return
        }
        if (enable_quoting && quotingAccess) {
            const quoteCustomisationTabValue = { label: 'Quote Customization', value: TAB_VALUES.QUOTE_CUSTOMISATION_TAB_VALUE }
            const editOptions = [...EDIT_LOC_DEFAULT_TAB_OPTIONS]
            editOptions.splice(3, 0, quoteCustomisationTabValue)
            setTabOptionsState(() => editOptions)
            return
        } else {
            setTabOptionsState(() => EDIT_LOC_DEFAULT_TAB_OPTIONS)
            return
        }
    }, [enable_quoting, quotingAccess, currentLocationId, isDuplicate, TAB_VALUES]);

    return (
        <>
            <Tabs onChange={handleTabChange} options={tabOptionsState} selectedTab={pathname} />
        </>
    );
};
